import React, { useState, useEffect } from "react";
import styles from "./css/UserModal.module.css";
import { Button } from "@/components/Buttons";
import { InputField } from "../inputFields/inputFields";
import SelectField from "../inputFields/SelectField";
import { RoleData, CityData, UserData } from "@/types";
import { AiOutlineClose } from "react-icons/ai";
import { useSelector, useDispatch } from "react-redux";
import { useCreateUser } from "@/hooks/user/useCreateUser";
import { useUpdateUser } from "@/hooks/user/useUpdateUser";
import { setToast } from "@/store/systemSlice";
import { RootState } from "@/store/store";
import { useTranslation } from 'react-i18next';
import { isValidEmail } from "@/utils/validations";

interface AddUserModalProps {
  isOpen: boolean;
  editUser?: UserData | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, editUser, onClose, onSuccess }) => {
  const { t } = useTranslation('AddUserModal');
  const { t: tRoles } = useTranslation('RolesNames');
  const dispatch = useDispatch();
  const isEditMode = !!editUser;
  const roles = useSelector((state: RootState) => state.systemService.roles).filter(
    (role) => role.name !== "super_admin"
  );
  const cities = useSelector((state: RootState) => state.entity.cities) || [];
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    user_name: ""
  });

  const [formData, setFormData] = useState({
    city_id: editUser?.city_id || "",
    user_name: editUser?.user_name || "",
    email: editUser?.email || "",
    first_name: editUser?.first_name || "",
    last_name: editUser?.last_name || "",
    role_id: editUser?.role_id || "",
  });

  useEffect(() => {
    if (editUser) {
      setFormData({
        city_id: editUser.city_id || "",
        user_name: editUser.user_name || "",
        email: editUser.email || "",
        first_name: editUser.first_name || "",
        last_name: editUser.last_name || "",
        role_id: editUser.role_id || "",
      });
    }
  }, [editUser]);

  const createUserMutation = useCreateUser({
    onSuccess: (data) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message,
          toastType: "success",
        })
      );
      onSuccess();
      resetForm();
    },
    onError: (error) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || "שגיאה ביצירת המשתמש",
          toastType: "error",
        })
      );
    },
  });

  const updateUserMutation = useUpdateUser({
    onSuccess: (data) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message,
          toastType: "success",
        })
      );
      onSuccess();
      resetForm();
    },
    onError: (error) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || "שגיאה בעדכון המשתמש",
          toastType: "error",
        })
      );
    },
  });

  const resetForm = () => {
    setFormData({
      city_id: "",
      user_name: "",
      email: "",
      first_name: "",
      last_name: "",
      role_id: "",
    });
    setErrors({ email: "", user_name: "" });
  };

  const handleDataChange = (field: string, value: string) => {
    let valid = true;
    
    if (field === "email") {
      if (!isValidEmail(value) && value !== "") {
        setErrors((prev) => ({
          ...prev,
          [field]: t("invalidEmail")
        }));
        valid = false;
      }
    }
    
    if (valid) {
      setErrors((prev) => ({
        ...prev,
        [field]: ""
      }));
    }
    
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRoleChange = (role: RoleData | undefined) => {
    setFormData((prev) => ({
      ...prev,
      role_id: role ? role.id : "",
    }));
  };

  const handleCityChange = (city: CityData | undefined) => {
    setFormData((prev) => ({
      ...prev,
      city_id: city ? city.id : "",
    }));
  };

  const handleCreate = () => {
    setLoading(true);
    if (isEditMode && editUser) {
      updateUserMutation.mutate({
        user_id: editUser.id,
        city_id: formData.city_id,
        user_name: formData.user_name,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role_id: formData.role_id,
      });
    } else {
      createUserMutation.mutate({
        ...formData,
        user_image: null,
      });
    }
  };

  const isFormValid = () => {
    if (isEditMode) {
      return (
        formData.first_name !== "" &&
        formData.last_name !== "" &&
        formData.user_name !== "" &&
        formData.city_id !== "" &&
        !errors.user_name
      );
    }
    return (
      formData.email !== "" &&
      formData.first_name !== "" &&
      formData.last_name !== "" &&
      formData.role_id !== "" &&
      formData.user_name !== "" &&
      formData.city_id !== "" &&
      !errors.email &&
      !errors.user_name
    );
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.container}>
      <div className={styles.modal}>
        <AiOutlineClose className={styles.closeButton} onClick={handleClose} />
        <p className={styles.username}>
          <span className={styles.usernameText}>{isEditMode ? t("editTitle") : t("title")}</span>
        </p>
        <div className={styles.rows}>
          <div className={`${styles.column} ${styles.fullWidth}`}>
            <div className={styles.row}>
              <div className={styles.inputLabel}>
                <label htmlFor="city">{t("city")}</label>
                <SelectField
                  data={cities}
                  selectedValue={cities.find((city) => city.id === formData.city_id) || {} as CityData}
                  updateFunction={handleCityChange}
                  showEmpty={true}
                />
              </div>
              
              <div className={styles.inputLabel}>
                <label htmlFor="username">{t("userName")}</label>
                <InputField
                  data={formData.user_name}
                  setData={(value) => handleDataChange("user_name", value || '')}
                  inputType="text"
                  id="username"
                  errorMessage={errors.user_name}
                />
              </div>
            </div>
            
            <div className={styles.row}>
              <div className={styles.inputLabel}>
                <label htmlFor="firstName">{t("firstName")}</label>
                <InputField
                  inputType="text"
                  data={formData.first_name}
                  setData={(value) => handleDataChange("first_name", value || '')}
                  id="firstName"
                />
              </div>
              
              <div className={styles.inputLabel}>
                <label htmlFor="lastName">{t("lastName")}</label>
                <InputField
                  inputType="text"
                  data={formData.last_name}
                  setData={(value) => handleDataChange("last_name", value || '')}
                  id="lastName"
                />
              </div>
            </div>

            {!isEditMode && (
              <div className={styles.row}>
                <div className={styles.inputLabel}>
                  <label htmlFor="email">{t("email")}</label>
                  <InputField
                    data={formData.email}
                    setData={(value) => handleDataChange("email", value || '')}
                    inputType="text"
                    id="email"
                    errorMessage={errors.email}
                  />
                </div>
                
                <div className={styles.inputLabel}>
                  <label htmlFor="userRole">{t("role")}</label>
                  <SelectField
                    data={roles.map(role => ({
                      ...role,
                      name: tRoles(role.name)
                    }))}
                    selectedValue={
                      roles.find((role) => role.id === formData.role_id) 
                        ? {
                            ...roles.find((role) => role.id === formData.role_id)!,
                            name: tRoles(roles.find((role) => role.id === formData.role_id)!.name)
                          }
                        : {} as RoleData
                    }
                    updateFunction={handleRoleChange}
                    showEmpty={true}
                  />
                </div>
              </div>
            )}

            <div className={styles.buttonContainer}>
              <Button
                icon="no"
                text={t("cancel")}
                popupstyle="gray"
                onClick={handleClose}
                disabled={loading}
                id="cancel-user-btn"
              />
              <Button
                icon="no"
                text={isEditMode ? t("updateButton") : t("createButton")}
                popupstyle="red"
                onClick={handleCreate}
                loading={loading}
                disabled={!isFormValid()}
                id={isEditMode ? "update-user-btn" : "create-user-btn"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
